import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../services/firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { LOADING_STYLES } from '../../constants';
import SellerSetup from './SellerSetup';
import DashboardContainer from './DashboardContainer';

const SellerDashboard = () => {
  const [user] = useAuthState(auth);
  const [sellerData, setSellerData] = useState(null);
  const [earnings, setEarnings] = useState({
    total: 0,
    thisMonth: 0,
    pending: 0,
    available: 0
  });
  const [recentSales, setRecentSales] = useState([]);
  const [activeListings, setActiveListings] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSellerData = async () => {
      if (!user) return;

      try {
        // Get user data first to check sellerStatus
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        let sellerStatusData = null;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          sellerStatusData = {
            sellerStatus: userData.sellerStatus || 'not_started',
            stripeAccountStatus: userData.stripeAccountStatus,
            stripeConnectAccountId: userData.stripeConnectAccountId
          };
        } else {
          sellerStatusData = { sellerStatus: 'not_started' };
        }

        // Get seller profile if exists
        const sellerDoc = await getDoc(doc(db, 'sellerProfiles', user.uid));
        if (sellerDoc.exists()) {
          const finalSellerData = { ...sellerDoc.data(), ...sellerStatusData };
          setSellerData(finalSellerData);
        } else {
          setSellerData(sellerStatusData);
        }

        // Get seller's transactions
        const transactionsQuery = query(
          collection(db, 'transactions'),
          where('sellerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const transactionsSnapshot = await getDocs(transactionsQuery);
        
        let totalEarnings = 0;
        let pendingEarnings = 0;
        let availableEarnings = 0;
        const salesData = [];

        transactionsSnapshot.forEach((doc) => {
          const transaction = { id: doc.id, ...doc.data() };
          const amount = transaction.sellerAmount || 0; // Amount in cents
          
          totalEarnings += amount;
          
          if (transaction.status === 'completed') {
            availableEarnings += amount;
          } else {
            pendingEarnings += amount;
          }
          
          if (salesData.length < 5) {
            salesData.push(transaction);
          }
        });

        // Get active listings count
        const listingsQuery = query(
          collection(db, 'listings'),
          where('sellerId', '==', user.uid),
          where('status', '==', 'active')
        );
        const listingsSnapshot = await getDocs(listingsQuery);
        setActiveListings(listingsSnapshot.size);

        // Convert amounts from cents to dollars for display
        setEarnings({
          total: totalEarnings / 100,
          pending: pendingEarnings / 100,
          available: availableEarnings / 100,
          thisMonth: 0 // Calculate this month if needed
        });
        setRecentSales(salesData);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchSellerData();
  }, [user]);

  if (loading) {
    return (
      <div style={LOADING_STYLES.container}>
        <div style={LOADING_STYLES.spinner}></div>
        <p>Loading seller dashboard...</p>
      </div>
    );
  }

  // Show setup page only if seller status is explicitly not active
  const isSellerActive = sellerData && sellerData.sellerStatus === 'active';
  
  if (!isSellerActive) {
    return <SellerSetup />;
  }

  return (
    <DashboardContainer 
      availableBalance={earnings.available}
      pendingBalance={earnings.pending}
      totalSales={earnings.total}
      activeListings={activeListings}
      recentSales={recentSales}
    />
  );
};

export default SellerDashboard;